import type { RouteStopTemplate } from "./types";

/**
 * Infers where an untracked bus sits on the route network from its raw GPS
 * fix alone — no trip, no claim, nothing from trip_instances. For the one
 * case that actually needs this: a vehicle moving at real speed with no
 * active trip (see resolveVehicleLiveState's "lost_contact" override). A bus
 * on a real trip already has an honest next stop from live-progress; this is
 * the fallback for when nothing is tracking the run at all.
 *
 * Method: among every (stop[i], stop[i+1]) segment across every known route
 * template, find the one the fix is geometrically closest to, and report its
 * two endpoints as "from" / "next" in the template's own stop-sequence order
 * — not by which endpoint happens to be nearer, since a bus can be fix-close
 * to a stop it already passed.
 */

export interface StopCoord {
  name: string;
  latitude: number;
  longitude: number;
}

export interface NearestSegmentMatch {
  fromStopId: string;
  fromStopName: string;
  toStopId: string;
  toStopName: string;
  distanceMeters: number;
  templateId?: string;
  headsign?: string;
}

const EARTH_RADIUS_M = 6371000;

/** Equirectangular approximation: accurate to well under 1% at city scale
 *  (a few km), and far simpler than projecting onto a great-circle segment. */
function toLocalMeters(lat: number, lon: number, originLat: number, originLon: number) {
  const dLat = ((lat - originLat) * Math.PI) / 180;
  const dLon = ((lon - originLon) * Math.PI) / 180;
  const x = dLon * Math.cos((originLat * Math.PI) / 180) * EARTH_RADIUS_M;
  const y = dLat * EARTH_RADIUS_M;
  return { x, y };
}

/** Shortest distance from point P to segment AB, all in the same local planar frame. */
function pointToSegmentMeters(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const abx = bx - ax;
  const aby = by - ay;
  const abLenSq = abx * abx + aby * aby;
  let t = abLenSq > 0 ? ((px - ax) * abx + (py - ay) * aby) / abLenSq : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * abx;
  const cy = ay + t * aby;
  const dx = px - cx;
  const dy = py - cy;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * @param stopsById Coordinates keyed by stop_id — from the existing
 * route-stops lookup. A template stop with no matching coordinate (stop
 * deleted/renamed since the template was built) is skipped, not fatal.
 */
export function matchNearestSegment(
  lat: number,
  lon: number,
  templates: RouteStopTemplate[],
  stopsById: Map<string, StopCoord>
): NearestSegmentMatch | undefined {
  let best: NearestSegmentMatch | undefined;

  for (const tpl of templates) {
    const stops = (tpl.stops ?? []).slice().sort((a, b) => a.stop_sequence - b.stop_sequence);
    for (let i = 0; i < stops.length - 1; i++) {
      const a = stopsById.get(stops[i].stop_id);
      const b = stopsById.get(stops[i + 1].stop_id);
      if (!a || !b) continue;

      // Project everything into a frame local to A — fine over one segment's
      // span (tens to low hundreds of meters between adjacent stops).
      const p = toLocalMeters(lat, lon, a.latitude, a.longitude);
      const bLocal = toLocalMeters(b.latitude, b.longitude, a.latitude, a.longitude);
      const distanceMeters = pointToSegmentMeters(p.x, p.y, 0, 0, bLocal.x, bLocal.y);

      if (!best || distanceMeters < best.distanceMeters) {
        best = {
          fromStopId: stops[i].stop_id,
          fromStopName: a.name,
          toStopId: stops[i + 1].stop_id,
          toStopName: b.name,
          distanceMeters,
          templateId: typeof tpl.id === "string" ? tpl.id : undefined,
          headsign: tpl.headsign,
        };
      }
    }
  }

  return best;
}

/** "Between N-5 and Golra Morr (120 m away)" — whichever parts are known. */
export function describeNearestSegment(m: NearestSegmentMatch | undefined): string | undefined {
  if (!m) return undefined;
  const dist = m.distanceMeters >= 1000 ? `${(m.distanceMeters / 1000).toFixed(1)} km` : `${Math.round(m.distanceMeters)} m`;
  return `Between ${m.fromStopName} and ${m.toStopName} (${dist} off route)`;
}
